import { MouseEvent, useCallback } from 'react'
import Link, { LinkProps } from '@mui/material/Link'
import { getBoxFileId, isModifierClick } from '../../../helpers/boxFileLink'
import {
  boxViewerModeFor,
  previewSyncRowProps,
  useOpenBoxPreview,
} from '../../../hooks/useBoxPreview'

interface IProps extends Omit<LinkProps, 'href' | 'target' | 'rel' | 'onClick'> {
  link?: string | null
  // Runs when a click opens the preview, e.g. to close the menu the link sits in.
  onPreview?: () => void
  'data-cy'?: string
}

/**
 * A link to a file in Box. A plain click previews the file without leaving the page, and
 * a modifier click still opens it in Box. Any other link opens in a new tab as before, so
 * callers can pass whatever link the record holds. No click on it reaches a row or cell
 * click handler, which would otherwise open the same document a second time.
 */
const BoxFileLink = ({ link, onPreview, ...linkProps }: IProps) => {
  const openPreview = useOpenBoxPreview()
  const isBoxFile = !!getBoxFileId(link)

  const handleClick = useCallback(
    (event: MouseEvent<HTMLAnchorElement>) => {
      event.stopPropagation()
      if (!isBoxFile || isModifierClick(event)) {
        return
      }
      event.preventDefault()
      onPreview?.()
      void openPreview(link, boxViewerModeFor(event.currentTarget))
    },
    [isBoxFile, link, onPreview, openPreview],
  )

  return (
    <Link
      {...linkProps}
      {...(isBoxFile ? previewSyncRowProps : undefined)}
      href={link || undefined}
      target="_blank"
      rel="noopener noreferrer"
      onClick={handleClick}
    />
  )
}

export default BoxFileLink
